/**
 * DimmingStar — Phase W sky-element sprite for OverworldScene.
 *
 * GDD §7.5 + §17 #16: a camera-fixed star sits above the overworld
 * sky. Alpha is driven by the server-authoritative `dimmingPct`
 * counter (`MSG_SKY_STATE`). The star never disappears — a floor
 * at 0.15 alpha keeps it present through 100 % dimming.
 *
 * Visual:
 *   - Procedural soft-circle drawn via a Graphics object → generated
 *     texture → Image sprite. A painted asset lands in a future art
 *     wave (Wave-2+); this is the MVP baseline.
 *   - Positioned top-right of the viewport (camera-fixed; scrollFactor 0).
 *   - Depth 40 — above CloudParallaxSystem's highest layer (30) and
 *     well below any HUD (1000+).
 *
 * Behaviour:
 *   - `setDimmingPct(pct)` tweens alpha to the new target over ~2 s
 *     so unexpected jumps (reconnect replay, full-moon broadcast)
 *     transition gracefully.
 *   - An idle ±5 % twinkle runs continuously over ~3 s so the star
 *     feels alive even at a fresh 0 % dimming.
 *
 * Non-goals reaffirmed from the brief:
 *   - No menu, tooltip, or interactive UI.
 *   - No per-realm variants.
 *   - No menu text — the sky element is purely ambient.
 *
 * @packageDocumentation
 */

import * as Phaser from 'phaser';
import { dimmingPctToAlpha } from './skyInterpolation';

/** Texture key for the procedural star — registered once per Phaser game. */
const STAR_TEXTURE_KEY = 'phase-w-dimming-star';

/** Pixel radius of the generated star texture. */
const STAR_RADIUS = 14;

/** Viewport inset (px) from the top-right corner. */
const STAR_INSET_X = 48;
const STAR_INSET_Y = 56;

/** Render depth — above clouds (max 30 in CloudParallaxSystem), below HUD. */
const STAR_DEPTH = 40;

/** Alpha tween duration (ms) on server-pushed dimming changes. */
const DIMMING_TWEEN_MS = 2000;

/** Twinkle oscillation amplitude (±% of target alpha). */
const TWINKLE_AMPLITUDE = 0.05;

/** Twinkle half-period (ms). Full cycle = 2 × this value. */
const TWINKLE_HALF_PERIOD_MS = 1500;

/**
 * DimmingStar — owns the star sprite + tweens. Instantiate from
 * `OverworldScene.create()` after the camera exists, and call
 * `setDimmingPct()` whenever `MSG_SKY_STATE` arrives.
 */
export class DimmingStar {
  private readonly scene: Phaser.Scene;
  private sprite: Phaser.GameObjects.Image | null = null;

  /** Current target alpha the dim tween is moving toward. */
  private targetAlpha = 1.0;

  /** Handle for the active dim-change tween (nullable — cancelled on re-target). */
  private alphaTween: Phaser.Tweens.Tween | null = null;

  /** Handle for the idle twinkle tween (looping). */
  private twinkleTween: Phaser.Tweens.Tween | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.ensureTexture();
    this.createSprite();
    this.startTwinkle();
  }

  /**
   * Apply a server-authoritative dimming percentage. Tweens alpha over
   * ~2 s so an unexpected jump (reconnect replay, full-moon broadcast)
   * transitions without a visible pop.
   */
  setDimmingPct(dimmingPct: number): void {
    if (!this.sprite) return;
    const nextAlpha = dimmingPctToAlpha(dimmingPct);
    this.targetAlpha = nextAlpha;

    // Cancel any in-flight dim tween — we want the most-recent target
    // to win, not the oldest. The twinkle tween is orthogonal; leave it
    // running so the star keeps breathing while the base alpha moves.
    if (this.alphaTween) {
      this.alphaTween.stop();
      this.alphaTween = null;
    }

    // Stop the twinkle so its alpha writes don't fight the dim tween
    // mid-flight. Restart after the dim tween completes.
    if (this.twinkleTween) {
      this.twinkleTween.stop();
      this.twinkleTween = null;
    }

    this.alphaTween = this.scene.tweens.add({
      targets: this.sprite,
      alpha: nextAlpha,
      duration: DIMMING_TWEEN_MS,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this.alphaTween = null;
        this.startTwinkle();
      },
    });
  }

  /** Destroy the sprite + cancel tweens. Idempotent. */
  destroy(): void {
    if (this.alphaTween) {
      this.alphaTween.stop();
      this.alphaTween = null;
    }
    if (this.twinkleTween) {
      this.twinkleTween.stop();
      this.twinkleTween = null;
    }
    if (this.sprite) {
      this.sprite.destroy();
      this.sprite = null;
    }
  }

  /**
   * Get the current target alpha — exposed for debugging / playtest
   * harnesses that want to assert the star is tracking the server
   * broadcast.
   */
  getTargetAlpha(): number {
    return this.targetAlpha;
  }

  // ── Internals ────────────────────────────────────────────────────────

  private ensureTexture(): void {
    if (this.scene.textures.exists(STAR_TEXTURE_KEY)) return;

    // Build a soft-circle texture by drawing concentric faded discs into
    // a Graphics object and rendering to a texture. Cheap, resolution-
    // independent, and gets replaced by a painted asset later.
    const size = STAR_RADIUS * 2;
    const g = this.scene.add.graphics({ x: 0, y: 0 });
    // Outer halo — faint, very soft edge.
    g.fillStyle(0xffffff, 0.15);
    g.fillCircle(STAR_RADIUS, STAR_RADIUS, STAR_RADIUS);
    // Mid ring — a bit brighter.
    g.fillStyle(0xfff6cf, 0.55);
    g.fillCircle(STAR_RADIUS, STAR_RADIUS, STAR_RADIUS * 0.55);
    // Core — near-pure white, small.
    g.fillStyle(0xffffff, 1.0);
    g.fillCircle(STAR_RADIUS, STAR_RADIUS, STAR_RADIUS * 0.25);
    g.generateTexture(STAR_TEXTURE_KEY, size, size);
    g.destroy();
  }

  private createSprite(): void {
    const cam = this.scene.cameras.main;
    // Position: top-right of the viewport, offset inward. Stays fixed to
    // the camera so it tracks the player without touching world coords.
    const x = cam.width - STAR_INSET_X;
    const y = STAR_INSET_Y;

    this.sprite = this.scene.add.image(x, y, STAR_TEXTURE_KEY);
    this.sprite.setScrollFactor(0);
    this.sprite.setDepth(STAR_DEPTH);
    this.sprite.setAlpha(this.targetAlpha);
    (this.scene as any).addToUI?.(this.sprite);
  }

  private startTwinkle(): void {
    if (!this.sprite) return;
    if (this.twinkleTween) return; // already running

    // Oscillate around the current target alpha by ±TWINKLE_AMPLITUDE.
    // Clamp inside the interpolation range so we don't push the star
    // above 1.0 or below the floor during twinkle.
    const low  = Math.max(0.05, this.targetAlpha - TWINKLE_AMPLITUDE);
    const high = Math.min(1.00, this.targetAlpha + TWINKLE_AMPLITUDE);

    this.sprite.setAlpha(this.targetAlpha);
    this.twinkleTween = this.scene.tweens.add({
      targets: this.sprite,
      alpha: { from: low, to: high },
      duration: TWINKLE_HALF_PERIOD_MS,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }
}
